// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { Component, type ErrorInfo, type ReactElement, type ReactNode } from "react";
import {
  Body1,
  Button,
  Subtitle2,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { reloadApp } from "../../utils/reloadApp";
import { StaleChunkLoadError } from "../../utils/staleChunkReload";

const useStyles = makeStyles({
  surface: {
    display: "grid",
    gap: tokens.spacingVerticalS,
    justifyItems: "start",
    maxWidth: "640px",
    margin: "0 auto",
    padding: tokens.spacingVerticalXXL,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "var(--riddle-surface-primary)",
  },
  summary: {
    color: tokens.colorNeutralForeground2,
  },
  detail: {
    color: tokens.colorPaletteRedForeground1,
    wordBreak: "break-word",
  },
});

export interface ErrorBoundaryProps {
  children: ReactNode;
  title?: string;
  description?: string;
}

interface CaughtFailure {
  title?: string;
  description?: string;
  detail?: string;
}

interface ErrorBoundaryState {
  failure: CaughtFailure | null;
}

function RecoverySurface({
  title,
  description,
  detail,
  onReload,
}: {
  title: string;
  description: string;
  detail: string | undefined;
  onReload: () => void;
}): ReactElement {
  const styles = useStyles();
  return (
    <section className={styles.surface} role="alert" aria-label={title}>
      <Subtitle2>{title}</Subtitle2>
      <Body1 className={styles.summary}>{description}</Body1>
      {detail !== undefined && <Body1 className={styles.detail}>{detail}</Body1>}
      <Button appearance="primary" onClick={onReload}>
        Reload
      </Button>
    </section>
  );
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { failure: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { failure: describeFailure(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(
      "[ui] unhandled render error",
      describeError(error instanceof StaleChunkLoadError ? error.cause : error),
      info.componentStack,
    );
  }

  private handleReload = (): void => {
    reloadApp();
  };

  render(): ReactNode {
    const { failure } = this.state;
    if (failure !== null) {
      return (
        <RecoverySurface
          title={failure.title ?? this.props.title ?? "Something went wrong on this page"}
          description={
            failure.description ??
            this.props.description ??
            "We could not finish loading this view. Reload to try again — if it keeps happening, send this message to support."
          }
          detail={failure.detail}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

function describeFailure(error: unknown): CaughtFailure {
  if (error instanceof StaleChunkLoadError) {
    return { title: error.title, description: error.message };
  }
  return { detail: describeError(error) };
}

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }
  if (typeof error === "string" && error.length > 0) {
    return error;
  }
  return "Unknown error.";
}